import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Phone, Truck, CheckCircle2, AlertTriangle, Loader2, LogOut, Flag, HelpCircle } from "lucide-react";
import { DriverHelpModal } from "@/components/StaffHelp";
import RoleWalkthrough from "@/components/RoleWalkthrough";

function SignaturePad({ onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);

  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const c = ref.current.getContext("2d"); const p = pos(e); c.beginPath(); c.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const c = ref.current.getContext("2d"); c.lineWidth = 2.5; c.lineCap = "round"; c.strokeStyle = "#0A1C40"; const p = pos(e); c.lineTo(p.x, p.y); c.stroke(); };
  const end = () => { if (drawing.current) { drawing.current = false; onChange(ref.current.toDataURL("image/png")); } };
  const clear = () => { const c = ref.current.getContext("2d"); c.clearRect(0, 0, ref.current.width, ref.current.height); onChange(null); };

  return (
    <div>
      <canvas ref={ref} width={320} height={130} className="border-2 border-dashed border-gray-300 bg-white w-full touch-none"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} data-testid="signature-pad" />
      <button type="button" onClick={clear} className="text-xs text-gray-500 underline mt-1" data-testid="signature-clear">Clear signature</button>
    </div>
  );
}

function DeliverModal({ stop, onClose, onDone }) {
  const [noDamage, setNoDamage] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [signature, setSignature] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!signature) return toast.error("Customer signature is required");
    if (!noDamage && !photo) return toast.error("Tick 'No damage' OR add a damage photo");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("signature", signature);
      fd.append("no_damage", noDamage ? "true" : "false");
      if (photo) fd.append("damage_photo", photo);
      await api.post(`/driver/stops/${stop.id}/deliver`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${stop.order_number} delivered`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-3" data-testid="deliver-modal">
      <div className="bg-white w-full max-w-md p-5 rounded-t-xl sm:rounded-xl max-h-[92vh] overflow-auto">
        <h2 className="font-display font-black text-xl">Close stop — {stop.order_number}</h2>
        <label className={`mt-4 flex items-start gap-3 border-2 p-3 cursor-pointer ${noDamage ? "border-green-600 bg-green-50" : "border-gray-200"}`}>
          <input type="checkbox" checked={noDamage} onChange={(e) => setNoDamage(e.target.checked)} className="mt-1 w-5 h-5 accent-green-600" data-testid="no-damage-checkbox" />
          <span className="text-sm"><b>No damage confirmed</b> — customer inspected the items and everything is in good condition.</span>
        </label>
        <div className="text-center text-xs text-gray-400 my-2">— OR —</div>
        <label className="block border-2 border-gray-200 p-3 text-sm cursor-pointer">
          <b>Damage photo</b> {photo ? <span className="text-green-700">✓ {photo.name}</span> : <span className="text-gray-500">(take a photo of the damage)</span>}
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setPhoto(e.target.files?.[0] || null)} data-testid="damage-photo-input" />
        </label>
        <div className="mt-4">
          <div className="text-sm font-bold mb-1">Customer signature <span className="text-red-600">*</span></div>
          <SignaturePad onChange={setSignature} />
        </div>
        <div className="grid grid-cols-2 gap-2 mt-4">
          <button onClick={onClose} className="border-2 border-gray-300 font-bold py-3 rounded-full">Cancel</button>
          <button onClick={submit} disabled={busy} className="bg-green-600 text-white font-black py-3 rounded-full disabled:opacity-50 flex items-center justify-center gap-2" data-testid="confirm-delivered-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />} DELIVERED
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DriverPage() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [delivering, setDelivering] = useState(null);
  const [exceptionFor, setExceptionFor] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const load = useCallback(() => {
    api.get("/driver/stops").then((r) => setData(r.data)).catch(() => toast.error("Couldn't load stops"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (stop, action) => {
    try {
      await api.post(`/driver/stops/${stop.id}/${action}`);
      toast.success(action === "start" ? "Customer notified — on the way" : "Arrival logged");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const sendException = async (stop, reason) => {
    try {
      const fd = new FormData();
      fd.append("reason", reason);
      fd.append("note", "");
      await api.post(`/driver/stops/${stop.id}/exception`, fd);
      toast.success("Store has been alerted — order stays open");
      setExceptionFor(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const mapsUrl = (s) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${s.address}, ${s.city}, ${s.state} ${s.zip}`)}`;

  return (
    <div className="min-h-screen bg-gray-100" data-testid="driver-page">
      <header className="bg-[#0A1C40] text-white px-4 py-3 flex items-center justify-between sticky top-0 z-40">
        <div>
          <div className="font-display font-black">IPVALUE DRIVER</div>
          <div className="text-[11px] text-white/60">{user?.name} · {data?.date || ""}</div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowHelp(true)} className="flex items-center gap-1 text-xs font-bold bg-white/10 hover:bg-white/20 px-2.5 py-1.5 rounded-full" data-testid="driver-help-btn">
            <HelpCircle className="w-4 h-4" /> Help
          </button>
          <button onClick={logout} className="text-white/70 hover:text-white" data-testid="driver-logout"><LogOut className="w-5 h-5" /></button>
        </div>
      </header>
      {showHelp && <DriverHelpModal onClose={() => setShowHelp(false)} />}
      <RoleWalkthrough />

      <div className="bg-red-600 text-white text-center text-sm font-black px-4 py-2.5 sticky top-[52px] z-40" data-testid="driver-no-money-banner">
        🚫 YOU DO NOT COLLECT MONEY. EVERY ORDER HERE IS PAID IN FULL.
      </div>

      <main className="p-3 max-w-lg mx-auto space-y-3 pb-16">
        {!data && <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}
        {data && data.stops.length === 0 && (
          <div className="text-center py-14 text-gray-500" data-testid="driver-no-stops">
            <Truck className="w-10 h-10 mx-auto text-gray-300" />
            <p className="mt-3 font-bold">No delivery stops today.</p>
          </div>
        )}
        {data && data.stops.map((s, i) => {
          const done = s.status === "completed";
          return (
            <div key={s.id} className={`bg-white border-2 rounded-lg p-4 ${done ? "border-green-500 opacity-70" : s.delivery_exception ? "border-amber-500" : "border-gray-200"}`} data-testid={`driver-stop-${s.order_number}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="ipv-eyebrow">Stop {i + 1} · {s.order_number}</div>
                  <div className="font-display font-black text-lg mt-0.5">{s.customer_name}</div>
                  <div className="text-sm text-gray-700">{s.address}, {s.city} {s.zip}</div>
                  {s.delivery_window && <div className="text-xs text-[#0A1C40] font-bold mt-0.5">🕘 {s.delivery_window}</div>}
                </div>
                {done && <CheckCircle2 className="w-7 h-7 text-green-600 shrink-0" />}
                {s.delivery_exception && !done && <Flag className="w-6 h-6 text-amber-600 shrink-0" />}
              </div>
              <div className="mt-2 text-sm text-gray-600 border-t border-gray-100 pt-2">
                {s.items.map((it, j) => <div key={j}>• {it.quantity} × {it.name}{it.color ? ` (${it.color})` : ""}</div>)}
                {s.notes && <div className="text-xs italic mt-1">Note: {s.notes}</div>}
              </div>
              {s.delivery_exception && !done && (
                <div className="mt-2 text-xs bg-amber-50 border border-amber-300 p-2 font-bold text-amber-800">
                  ⚠ Flagged: {s.delivery_exception.reason === "not_home" ? "customer not home" : "customer refused"} — store notified
                </div>
              )}
              {!done && (
                <>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <a href={mapsUrl(s)} target="_blank" rel="noreferrer" className="border-2 border-[#0A1C40] text-[#0A1C40] font-bold py-2.5 rounded-full text-center text-sm flex items-center justify-center gap-1.5" data-testid={`directions-${s.order_number}`}>
                      <MapPin className="w-4 h-4" /> Directions
                    </a>
                    {s.customer_phone ? (
                      <a href={`tel:${s.customer_phone}`} className="border-2 border-[#0A1C40] text-[#0A1C40] font-bold py-2.5 rounded-full text-center text-sm flex items-center justify-center gap-1.5" data-testid={`call-${s.order_number}`}>
                        <Phone className="w-4 h-4" /> Call
                      </a>
                    ) : <div className="text-center text-xs text-gray-400 self-center">No phone</div>}
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {s.status !== "out_for_delivery" ? (
                      <button onClick={() => act(s, "start")} className="bg-[#0A1C40] text-white font-bold py-2.5 rounded-full text-sm" data-testid={`start-${s.order_number}`}>
                        🚚 Start delivery
                      </button>
                    ) : !s.driver_arrived_at ? (
                      <button onClick={() => act(s, "arrived")} className="bg-[#FFC220] text-[#0A1C40] font-bold py-2.5 rounded-full text-sm" data-testid={`arrived-${s.order_number}`}>
                        📍 Arrived
                      </button>
                    ) : (
                      <div className="text-center text-xs text-green-700 font-bold self-center">Arrived ✓</div>
                    )}
                    <button onClick={() => setDelivering(s)} disabled={s.status !== "out_for_delivery"}
                      className="bg-green-600 text-white font-black py-2.5 rounded-full text-sm disabled:opacity-40" data-testid={`deliver-${s.order_number}`}>
                      ✓ Mark DELIVERED
                    </button>
                  </div>
                  {exceptionFor === s.id ? (
                    <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                      <button onClick={() => sendException(s, "not_home")} className="border-2 border-amber-500 text-amber-700 font-bold py-2 rounded-full" data-testid={`not-home-${s.order_number}`}>Not home</button>
                      <button onClick={() => sendException(s, "refused")} className="border-2 border-red-500 text-red-700 font-bold py-2 rounded-full" data-testid={`refused-${s.order_number}`}>Refused</button>
                      <button onClick={() => setExceptionFor(null)} className="border border-gray-300 py-2 rounded-full">Cancel</button>
                    </div>
                  ) : (
                    <button onClick={() => setExceptionFor(s.id)} className="mt-2 w-full text-xs text-amber-700 font-bold flex items-center justify-center gap-1" data-testid={`exception-${s.order_number}`}>
                      <AlertTriangle className="w-3.5 h-3.5" /> Customer not home / refused
                    </button>
                  )}
                </>
              )}
              {done && <div className="mt-2 text-xs text-green-700 font-bold">Delivered {s.delivered_at ? new Date(s.delivered_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""} · signature on file</div>}
            </div>
          );
        })}
      </main>

      {delivering && <DeliverModal stop={delivering} onClose={() => setDelivering(null)} onDone={() => { setDelivering(null); load(); }} />}
    </div>
  );
}
